<template>
  <div
    class="groups-query-list card"
    :class="{ '-loading': loading }"
  >
    <loader v-if="loading"></loader>

    <div class="header">
      <div class="description">
        <h2 class="title">{{ title }}</h2>
        <p class="subtitle">{{ subtitle }}</p>
      </div>

      <card-actions v-if="createRoute">
        <router-link
            :to="createRoute"
            class="btn btn-primary -sm"
        >Novo Template<ion-icon name="add"></ion-icon></router-link>
      </card-actions>
    </div>

    <div
      class="body"
      :class="{ '-no-padding' : !errors.cantLoadList && !errors.cantLoadGroupQueriesList }"
    >
      <div class="alert -danger" v-if="errors.cantLoadList">
        <p class="title">Ops!</p>

        <p>
          Infelizmente aconteceu um erro ao tentar carregar suas consultas,
          por favor tente novamente ou contate nossa equipe.
        </p>
      </div>
      <div class="alert -danger" v-if="errors.cantLoadGroupQueriesList">
        <template slot="title">Ops!</template>

        <p>
          Infelizmente aconteceu um erro ao tentar exibir os detalhes,
          por favor tente novamente ou contate nossa equipe.
        </p>
      </div>

      <template v-if="!errors.cantLoadList">
        <table class="list">
          <thead>
          <tr class="-head">
            <th>Nome</th>
            <th>Nº consultas</th>
            <th>Valor estimado</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <template v-for="template in templates.data">
            <tr
              class="row"
              :class="{ '-expanded': groupExpanded === template.id }"
            >
              <td class="cell -queryinfo">{{ template.name }}</td>
              <td class="cell -queryinfo">{{ template.consultations.length }}</td>
              <td class="cell -queryinfo">R$ {{ estimatedValue(template.consultations.length) | currency }}</td>
              <td class="cell -expanseaction">
                <button class="btn -sm btn-primary" title="Editar template" @click="editTemplate(template.id)">
                  <ion-icon name="pencil" role="img" class="xs hydrated"></ion-icon>
                </button>
                <button class="btn -sm btn-danger" title="Excluir" @click="deleteTemplate(template.id)">
                  <ion-icon name="trash" role="img" class="xs hydrated"></ion-icon>
                </button>
              </td>
            </tr>
          </template>
          </tbody>
        </table>

        <div class="navList">
          <pagination
            :limit="1"
            :data="templates"
            @pagination-change-page="page = $event"
          >
            <span slot="prev-nav">Anterior</span>
            <span slot="next-nav">Próxima</span>
          </pagination>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import queriesCompletedText from '@/compliance/filters/queriesCompletedText';
import Loader from '@/support/components/Loader.vue';
import CardActions from '@/support/components/card/CardActions.vue';
import apiLink from '@/support/filters/apiLink';
import currency from '@/support/filters/currency';
import dateFormat from '@/support/filters/dateFormat';
import Pagination from 'laravel-vue-pagination';
import { mapState } from 'vuex';

export default {
  name: 'List',

  data() {
    return {
      loading: false,
      page: 1,
      groupExpanded: '',
      canSelectAllQueriesToDownload: false,
      errors: {
        cantLoadList: false,
        cantLoadGroupQueriesList: false,
      },
    };
  },

  props: {
    tag: {
      required: true,
      type: String,
    },
    title: {
      required: true,
      type: String,
    },
    subtitle: {
      required: false,
      type: String,
    },
    createRoute: {
      required: false,
    },
  },

  methods: {
    /**
     * Fetch the groups list.
     *
     * @returns {Promise<void>}
     */
    async fetchTemplatesList() {
      try {
        this.resetErrors();
        this.loading = true;

        await this.$store.dispatch(
          'template/fetchTemplateList',
          { tag: this.tag, page: this.page },
        );
      } catch {
        this.errors.cantLoadList = true;
      } finally {
        this.loading = false;
      }
    },

    async editTemplate(id) {
      await this.$router.push({
        name: {
          cpf: 'Template-Physical-Person-Edit',
          cnpj: 'Template-Legal-Person-Edit',
          vehicles: 'Template-Vehicle-Edit',
          properties: 'Template-Property-Edit',
          others: 'Template-Other-Edit',
        }[this.tag],
        params: { id },
      });
    },

    async deleteTemplate(id) {
      this.loading = true;
      await this.$store.dispatch('template/delete', id);
      this.$notify({
        group: 'app',
        type: 'success',
        title: 'Sucesso!',
        text: 'Template deletado com sucesso.',
      });
      await this.fetchTemplatesList();
      this.loading = false;
    },

    /**
     * Reset the all errors to initial value.
     */
    resetErrors() {
      this.errors.cantLoadList = false;
      this.errors.cantLoadGroupQueriesList = false;
    },

    estimatedValue(length) {
      return length * this.user.plan.price_per_consultation;
    },
  },

  computed: {
    ...mapState('template', {
      templates: 'templates',
    }),
    ...mapState('auth', {
      accessToken: 'accessToken',
      user: 'user',
    }),
  },

  mounted() {
    this.fetchTemplatesList();
  },

  watch: {
    groupExpanded() {
      this.queriesToDownload = [];
    },

    page() {
      this.fetchTemplatesList();
    },
  },

  filters: {
    currency,
    dateFormat,
    queriesCompletedText,
    apiLink,
  },

  components: {
    Pagination,
    CardActions,
    Loader,
  },
};
</script>

<style lang="scss" scoped>
  .groups-query-list {
    > .body {
      > table.list {
        > thead,
        > tbody {
          > tr {
            > th,
            > td {
              padding-top: 0.8rem;
              padding-bottom: 0.8rem;

              .form-group {
                @apply mb-0;
              }

              &.-expanseaction {
                @apply text-center;
                @apply w-full;
                display: flex;
                justify-content: flex-end;
                gap: 8px;

                .btn {
                  //padding: 0.2rem 0.5rem;

                  > ion-icon {
                    left: 0;
                  }
                }
              }

              &.-selectaction {
                .form-group {
                  @apply mb-0;
                }
              }

              &.-queryinfo {
                @apply text-sm;
                @apply text-secondary-900;
              }

              &.-statusinfo {
                white-space: nowrap;
                @apply pr-1;
              }
            }

            &.-head {
              @apply text-left;

              > .cell {
                @apply text-secondary-800;
                @apply text-xs;
                @apply font-bold;
              }
            }

            &.-detail,
            &.-expanded {
              @apply border-b-0;
              @apply border-t-0;
              @apply bg-primary-100;
              @apply bg-opacity-50;
            }

            &.-detail {
              &:last-child {
                @apply border-b;
              }
            }
          }
        }
      }

      > .navList {
        @apply flex;
        @apply justify-center;
      }
    }
  }
</style>
